import { useRef, useState, type RefObject } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  CheckCircle,
  Loader2,
  Calendar,
  Clock,
  MapPin,
  User,
  Building2,
  XCircle,
} from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/atoms/card';
import { Input } from '@/components/atoms/input';
import { Separator } from '@/components/atoms/separator';
import { PublicLayout } from '@/components/public/PublicLayout';
import { DocumentNotFound } from '@/components/public/DocumentNotFound';
import SignaturePad, { type SignaturePadHandle } from '@/components/molecules/SignaturePad';
import { publicGet, publicPost } from '@/lib/api/publicClient';
import { type PublicJobConfirmation } from '@/types/public';
import { dateStringToDate, formatDateTimeInTimeZone, formatTimeZoneLabel } from '@/lib/date-values';

export const Route = createFileRoute('/(public)/confirm-job/$referenceNo')({
  // §16 (C-SEC) — the scoped ownership token carried by the customer's link. The
  // backend verifies it so a guessed reference number can't forge a confirm /
  // authorization / sign-off.
  validateSearch: (search: Record<string, unknown>): { token?: string } => ({
    token: typeof search.token === 'string' ? search.token : undefined,
  }),
  component: ConfirmJobPage,
});

// Append the C-SEC token as a query param (confirm GET/POST take it on the query).
function withToken(url: string, token?: string): string {
  return token ? `${url}?token=${encodeURIComponent(token)}` : url;
}

type PublicSignatureUploadScope = 'authorization' | 'signoff';

async function uploadPublicSignatureImage(
  dataUrl: string,
  token: string | undefined,
  scope: PublicSignatureUploadScope,
): Promise<string> {
  if (!token) {
    throw new Error('Missing authorization token.');
  }
  const blob = await (await fetch(dataUrl)).blob();
  const { uploadUrl, fileUrl } = await publicPost<{
    uploadUrl: string;
    fileUrl: string;
  }>('/api/public/authorization/signature-upload-url', {
    token,
    scope,
    fileSize: blob.size,
  });
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    body: blob,
    headers: { 'Content-Type': 'image/png' },
  });
  if (!res.ok) {
    throw new Error(`Signature upload failed (${res.status})`);
  }
  return fileUrl;
}

function ConfirmJobPage() {
  const { referenceNo } = Route.useParams();
  const { token } = Route.useSearch();
  const queryClient = useQueryClient();
  const [confirmed, setConfirmed] = useState(false);
  const [authorizedBy, setAuthorizedBy] = useState('');
  const [signedByName, setSignedByName] = useState('');
  const [authorizationUploading, setAuthorizationUploading] = useState(false);
  const [signoffUploading, setSignoffUploading] = useState(false);
  const authorizationSigPadRef = useRef<SignaturePadHandle>(null);
  const signoffSigPadRef = useRef<SignaturePadHandle>(null);

  const {
    data: job,
    isLoading,
    isError,
  } = useQuery<PublicJobConfirmation>({
    queryKey: ['public', 'confirm-job', referenceNo, token],
    queryFn: () =>
      publicGet<PublicJobConfirmation>(withToken(`/api/public/jobs/confirm/${referenceNo}`, token)),
    retry: false,
  });

  const confirmMutation = useMutation({
    mutationFn: () => publicPost(withToken(`/api/public/jobs/confirm/${referenceNo}`, token)),
    onSuccess: () => {
      setConfirmed(true);
      toast.success('Job confirmed! See you then.');
    },
    onError: () => toast.error('Failed to confirm. Please try again.'),
  });

  // §16 (C-SEC / F2c) — the collect POST CONSUMES the single-use action token, so
  // we must NOT refetch the page with that (now dead) token afterwards — that would
  // 'already used' → "Job not found". Instead patch the cached confirmation locally
  // from the just-collected result so the panel flips to collected in place.
  const queryKey = ['public', 'confirm-job', referenceNo, token] as const;

  const authorizationMutation = useMutation({
    mutationFn: ({ signatureUrl }: { signatureUrl?: string } = {}) => {
      const endpoint =
        job?.authorization?.level === 'APPOINTMENT' && job.appointmentId
          ? `/api/public/appointments/${job.appointmentId}/authorization/collect`
          : `/api/public/jobs/${referenceNo}/authorization/collect`;
      return publicPost(endpoint, {
        method: job?.authorization?.method ?? 'LINK',
        authorizedBy: authorizedBy.trim(),
        signatureUrl,
        token,
      });
    },
    onSuccess: () => {
      const collectedBy = authorizedBy.trim();
      setAuthorizedBy('');
      authorizationSigPadRef.current?.clear();
      toast.success('Authorization recorded.');
      queryClient.setQueryData<PublicJobConfirmation>(queryKey, (old) =>
        old?.authorization
          ? {
              ...old,
              authorization: { ...old.authorization, collected: true, collectedBy },
            }
          : old,
      );
    },
    onError: () => toast.error('Failed to record authorization.'),
  });

  const signoffMutation = useMutation({
    mutationFn: ({ signatureUrl }: { signatureUrl?: string } = {}) => {
      const endpoint =
        job?.signoff?.level === 'APPOINTMENT' && job.appointmentId
          ? `/api/public/appointments/${job.appointmentId}/signoff/collect`
          : `/api/public/jobs/${referenceNo}/signoff/collect`;
      return publicPost(endpoint, {
        method: job?.signoff?.method ?? 'LINK',
        signedByName: signedByName.trim(),
        signatureUrl,
        token,
      });
    },
    onSuccess: () => {
      const collectedBy = signedByName.trim();
      setSignedByName('');
      signoffSigPadRef.current?.clear();
      toast.success('Sign-off recorded.');
      queryClient.setQueryData<PublicJobConfirmation>(queryKey, (old) =>
        old?.signoff ? { ...old, signoff: { ...old.signoff, collected: true, collectedBy } } : old,
      );
    },
    onError: () => toast.error('Failed to record sign-off.'),
  });

  async function submitAuthorization() {
    if (!authorizedBy.trim() || authorizationMutation.isPending || authorizationUploading) return;

    let signatureUrl: string | undefined;
    if (job?.authorization?.method === 'SIGNATURE') {
      const dataUrl = authorizationSigPadRef.current?.getDataURL();
      if (!dataUrl) {
        toast.error('Please draw your signature.');
        return;
      }
      try {
        setAuthorizationUploading(true);
        signatureUrl = await uploadPublicSignatureImage(dataUrl, token, 'authorization');
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Signature upload failed.');
        return;
      } finally {
        setAuthorizationUploading(false);
      }
    }

    authorizationMutation.mutate({ signatureUrl });
  }

  async function submitSignoff() {
    if (!signedByName.trim() || signoffMutation.isPending || signoffUploading) return;

    let signatureUrl: string | undefined;
    if (job?.signoff?.method === 'SIGNATURE') {
      const dataUrl = signoffSigPadRef.current?.getDataURL();
      if (!dataUrl) {
        toast.error('Please draw your signature.');
        return;
      }
      try {
        setSignoffUploading(true);
        signatureUrl = await uploadPublicSignatureImage(dataUrl, token, 'signoff');
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Signature upload failed.');
        return;
      } finally {
        setSignoffUploading(false);
      }
    }

    signoffMutation.mutate({ signatureUrl });
  }

  if (isLoading) {
    return (
      <PublicLayout>
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </PublicLayout>
    );
  }

  if (isError || !job) {
    return <DocumentNotFound message="Job not found" />;
  }

  const alreadyConfirmed = job.confirmed || job.status === 'confirmed' || confirmed;

  return (
    <PublicLayout>
      <Card className="shadow-sm">
        {/* Header */}
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            {job.business?.logo ? (
              <img
                src={job.business.logo}
                alt={job.business.name}
                className="h-12 w-12 rounded-lg object-contain"
                loading="lazy"
              />
            ) : (
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gray-100">
                <Building2 className="h-6 w-6 text-gray-400" />
              </div>
            )}
            <div>
              <p className="font-semibold text-gray-900">
                {job.business?.name ?? 'Your Service Provider'}
              </p>
              {job.business?.phone && <p className="text-sm text-gray-500">{job.business.phone}</p>}
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-6 pt-0">
          <div>
            <CardTitle className="mb-1 text-lg">Appointment Confirmation</CardTitle>
            <p className="text-sm text-gray-500">Reference #{job.referenceNo}</p>
          </div>

          <Separator />

          {/* Job details */}
          <div className="space-y-4 text-sm">
            {job.serviceType && (
              <div className="flex items-start gap-3">
                <Building2 className="mt-0.5 h-4 w-4 text-gray-400" />
                <div>
                  <p className="text-gray-500">Service</p>
                  <p className="font-medium text-gray-900">{job.serviceType}</p>
                </div>
              </div>
            )}

            {job.scheduledDate && (
              <div className="flex items-start gap-3">
                <Calendar className="mt-0.5 h-4 w-4 text-gray-400" />
                <div>
                  <p className="text-gray-500">Date</p>
                  <p className="font-medium text-gray-900">
                    {format(dateStringToDate(job.scheduledDate)!, 'EEEE, MMMM d, yyyy')}
                  </p>
                </div>
              </div>
            )}

            {job.scheduledStart && (
              <div className="flex items-start gap-3">
                <Clock className="mt-0.5 h-4 w-4 text-gray-400" />
                <div>
                  <p className="text-gray-500">Time</p>
                  <p className="font-medium text-gray-900">
                    {job.scheduledStart}
                    {job.scheduledEnd && ` – ${job.scheduledEnd}`}
                  </p>
                </div>
              </div>
            )}

            {job.address && (
              <div className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-4 w-4 text-gray-400" />
                <div>
                  <p className="text-gray-500">Location</p>
                  <p className="font-medium text-gray-900">{job.address}</p>
                </div>
              </div>
            )}

            {job.technician?.name && (
              <div className="flex items-start gap-3">
                <User className="mt-0.5 h-4 w-4 text-gray-400" />
                <div>
                  <p className="text-gray-500">Technician</p>
                  <p className="font-medium text-gray-900">{job.technician.name}</p>
                </div>
              </div>
            )}
          </div>

          <Separator />

          {((job.canAuthorize && job.authorization?.required) ||
            (job.canSignoff && job.signoff?.required)) && (
            <>
              <div className="space-y-3">
                {job.canAuthorize && job.authorization?.required && (
                  <PublicAuthorizationPanel
                    collected={job.authorization.collected}
                    collectedBy={job.authorization.collectedBy}
                    method={job.authorization.method ?? 'LINK'}
                    scopeSummary={job.authorization.scopeSummary}
                    maxAmount={job.authorization.maxAmount}
                    value={authorizedBy}
                    onChange={setAuthorizedBy}
                    submitting={authorizationMutation.isPending || authorizationUploading}
                    signaturePadRef={authorizationSigPadRef}
                    onSubmit={submitAuthorization}
                  />
                )}
                {job.canSignoff && job.signoff?.required && (
                  <PublicSignoffPanel
                    collected={job.signoff.collected}
                    collectedBy={job.signoff.collectedBy}
                    method={job.signoff.method ?? 'LINK'}
                    value={signedByName}
                    onChange={setSignedByName}
                    submitting={signoffMutation.isPending || signoffUploading}
                    signaturePadRef={signoffSigPadRef}
                    onSubmit={submitSignoff}
                  />
                )}
              </div>
              <Separator />
            </>
          )}

          {alreadyConfirmed ? (
            <div className="flex flex-col items-center gap-2 py-2 text-center">
              <CheckCircle className="h-10 w-10 text-green-500" />
              <p className="font-semibold text-gray-900">Appointment Confirmed</p>
              {job.confirmedAt && (
                <p className="text-xs text-gray-400">
                  Confirmed on{' '}
                  {formatDateTimeInTimeZone(job.confirmedAt, job.timezone ?? undefined, {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                  {job.timezone ? ` ${formatTimeZoneLabel(job.timezone)}` : ''}
                </p>
              )}
            </div>
          ) : job.status === 'cancelled' ? (
            <div className="flex flex-col items-center gap-2 py-2 text-center">
              <XCircle className="h-10 w-10 text-red-400" />
              <p className="font-semibold text-gray-900">This appointment has been cancelled.</p>
              <p className="text-sm text-gray-500">Please contact us to reschedule.</p>
            </div>
          ) : job.canConfirm ? (
            <div className="space-y-3">
              <Button
                className="w-full"
                size="lg"
                onClick={() => confirmMutation.mutate()}
                disabled={confirmMutation.isPending}
              >
                {confirmMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle className="mr-2 h-4 w-4" />
                )}
                Confirm Appointment
              </Button>
              {job.business?.phone && (
                <p className="text-center text-xs text-gray-400">
                  Questions? Call{' '}
                  <a href={`tel:${job.business.phone}`} className="text-blue-600 underline">
                    {job.business.phone}
                  </a>
                </p>
              )}
            </div>
          ) : job.business?.phone ? (
            <p className="text-center text-xs text-gray-400">
              Questions? Call{' '}
              <a href={`tel:${job.business.phone}`} className="text-blue-600 underline">
                {job.business.phone}
              </a>
            </p>
          ) : null}
        </CardContent>
      </Card>
    </PublicLayout>
  );
}

function PublicAuthorizationPanel({
  collected,
  collectedBy,
  method,
  scopeSummary,
  maxAmount,
  value,
  onChange,
  submitting,
  signaturePadRef,
  onSubmit,
}: {
  collected: boolean;
  collectedBy?: string;
  method: 'SIGNATURE' | 'LINK' | 'VERBAL';
  scopeSummary?: string;
  maxAmount?: number;
  value: string;
  onChange: (value: string) => void;
  submitting: boolean;
  signaturePadRef: RefObject<SignaturePadHandle | null>;
  onSubmit: () => void;
}) {
  if (collected) {
    return (
      <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">
        <CheckCircle className="mr-2 inline h-4 w-4" />
        Authorized{collectedBy ? ` by ${collectedBy}` : ''}.
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-lg border p-3">
      <div>
        <p className="font-medium text-gray-900">Work authorization required</p>
        {scopeSummary && <p className="mt-1 text-sm text-gray-500">{scopeSummary}</p>}
        {typeof maxAmount === 'number' && (
          <p className="mt-1 text-xs text-gray-400">
            Authorized amount up to{' '}
            {new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
            }).format(maxAmount)}
          </p>
        )}
      </div>
      {method === 'SIGNATURE' && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700">Signature</p>
          <SignaturePad ref={signaturePadRef} disabled={submitting} />
        </div>
      )}
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder="Your name"
        />
        <Button
          type="button"
          disabled={submitting || !value.trim()}
          onClick={onSubmit}
          className="sm:w-auto"
        >
          {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Authorize
        </Button>
      </div>
    </div>
  );
}

function PublicSignoffPanel({
  collected,
  collectedBy,
  method,
  value,
  onChange,
  submitting,
  signaturePadRef,
  onSubmit,
}: {
  collected: boolean;
  collectedBy?: string;
  method: 'SIGNATURE' | 'LINK' | 'IN_PERSON';
  value: string;
  onChange: (value: string) => void;
  submitting: boolean;
  signaturePadRef: RefObject<SignaturePadHandle | null>;
  onSubmit: () => void;
}) {
  if (collected) {
    return (
      <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">
        <CheckCircle className="mr-2 inline h-4 w-4" />
        Signed off{collectedBy ? ` by ${collectedBy}` : ''}.
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-lg border p-3">
      <div>
        <p className="font-medium text-gray-900">Completion sign-off required</p>
        <p className="mt-1 text-sm text-gray-500">Confirm the work was completed and accepted.</p>
      </div>
      {method === 'SIGNATURE' && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700">Signature</p>
          <SignaturePad ref={signaturePadRef} disabled={submitting} />
        </div>
      )}
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder="Your name"
        />
        <Button
          type="button"
          disabled={submitting || !value.trim()}
          onClick={onSubmit}
          className="sm:w-auto"
        >
          {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Sign Off
        </Button>
      </div>
    </div>
  );
}
